import { useState } from 'react'

import Image from 'next/image'
import Link from 'next/link'

import linkResolver from '@lib/prismic/link-resolver'

import RichTextSerializer from '@components/RichTextSerializer'
import VideoModal from '@components/VideoModal'
import PlayButton from '@components/icons/PlayButton'

interface Props {
	slice: any
	sliceIndex: number
}

export default function Split3({ slice, sliceIndex }: Props) {
	const { primary } = slice

	const [isVideoModalOpen, toggleVideoModal] = useState(false)

	const ctaResolvedHref = primary?.section_cta ? linkResolver(primary.section_cta) : ''

	return (
		<section className="relative section">
			{/* Colored background */}
			<div
				className="absolute top-0 left-0 right-10 sm:right-1/4 bottom-[32%] xs:bottom-[30%] sm:bottom-1/4 lg:bottom-1/3 bg-bbx-neon-green -z-10"
				aria-hidden="true"
			/>

			<div className="container flex max-lg:flex-col">
				<div className="flex flex-col lg:justify-between pt-10 lg:pt-20 lg:pb-20 max-w-[300px] max-lg:pl-2 sm:max-w-[420px] w-full">
					{/* Title */}
					{primary?.section_title?.[0]?.text && (
						<h2 className="text-4xl font-medium lg:text-7xl title">
							{primary.section_title[0].text}
						</h2>
					)}

					{/* Subheader */}
					{primary?.section_subheader && (
						<div className="mt-4">
							<RichTextSerializer content={primary.section_subheader} />
						</div>
					)}

					{/* Mobile description */}
					{primary?.section_description?.[0] && (
						<div className="mt-8 lg:hidden">
							<RichTextSerializer content={primary.section_description} />
						</div>
					)}

					{/* CTAs */}
					{primary.section_cta || primary?.section_video_id ? (
						<div className="flex mt-20 max-md:items-start max-md:flex-col gap-y-6 gap-x-8 ">
							{/* Primary CTA */}
							{primary.section_cta && (
								<Link legacyBehavior href={ctaResolvedHref}>
									<a className="font-medium button button-primary">{primary.section_cta_label}</a>
								</Link>
							)}

							{/* Video CTA */}
							{primary?.section_video_id && (
								<button
									type="button"
									className="flex items-center text-lg font-medium gap-x-3"
									onClick={() => toggleVideoModal(true)}
								>
									<PlayButton className="w-6 fill-bbx-style" />
									Watch Video
								</button>
							)}
						</div>
					) : null}
				</div>

				{primary.section_image?.url &&
					//  Section video
					(primary?.section_video_id ? (
						<button
							type="button"
							className="relative inline-flex mt-auto lg:mr-8 max-lg:mr-auto xl:mr-24 lg:ml-auto shrink max-lg:order-first"
							onClick={() => toggleVideoModal(true)}
						>
							{/* Image as video thumbnail */}
							<Image
								src={primary.section_image.url}
								width={primary.section_image.dimensions.width}
								height={primary.section_image.dimensions.height}
								alt={primary.section_image.alt}
								style={{ objectFit: 'contain' }}
								objectPosition="left"
								priority={sliceIndex === 0}
								quality={100}
								draggable={false}
							/>

							{/* Play icon */}
							<PlayButton className="absolute z-10 w-16 bottom-8 right-8 fill-white" />
						</button>
					) : (
						// Section image (no video)
						<div className="inline-flex mt-auto lg:mr-8 max-lg:mr-auto xl:mr-24 lg:ml-auto shrink max-lg:order-first">
							<Image
								src={primary.section_image.url}
								width={primary.section_image.dimensions.width}
								height={primary.section_image.dimensions.height}
								alt={primary.section_image.alt}
								className="object-contain"
								objectPosition="left"
								priority={sliceIndex === 0}
								quality={100}
							/>
						</div>
					))}

				{/* Desktop description */}
				{primary?.section_description?.[0] && (
					<div className="max-lg:hidden max-w-[160px] w-full self-end">
						<RichTextSerializer content={primary.section_description} />
					</div>
				)}
			</div>

			{/* Video modal */}
			{primary?.section_video_id && (
				<VideoModal
					isOpen={isVideoModalOpen}
					close={() => toggleVideoModal(false)}
					videoPlatform={primary.section_video_platform}
					videoId={primary.section_video_id}
				/>
			)}
		</section>
	)
}
